import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import close from 'assets/filters/close.svg';
import userActive from 'assets/tickets/user-active.svg';
import user from 'assets/tickets/user.svg';
import usersActive from 'assets/tickets/users-active.svg';
import users from 'assets/tickets/users.svg';
import watchActive from 'assets/tickets/watch-active.svg';
import watch from 'assets/tickets/watch.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import ResetButton from 'common-ui/resetButton';
import { Select } from 'common-ui/select';
import { TooltipText } from 'common-ui/tooltip/Tooltip.styles';
import { APPOINTMENT_TYPE_OPTIONS } from 'constants/constants';
import dayjs from 'dayjs';
import {
  getAssigneesForFilter,
  getCreatedByForFilter,
  getProjectsForFilter,
  getSubprojectsForFilter,
} from 'features/projectManagement/ProjectManagementActions';
import {
  selectAppointmentAssignTypes,
  selectAssigneesForFilter,
  selectCreatedByForFilter,
  selectProjectsForFilter,
  selectSubprojectsForFilter,
} from 'features/projectManagement/ProjectManagementSlice';
import useDebounce from 'hooks/useDebounce';
import FilterSelect from 'pages/components/filterSelect';
import { ClearAllText } from 'pages/userManagement/UserManagement.styles';

import {
  ActionsWrapper,
  AppointmentTypeSelectWrapper,
  BottomSheet,
  CalendarTypeWrapper,
  CalendarWrapper,
  CloseIconWrapper,
  Column,
  FilterActionsWrapper,
  FilterAllWrapper,
  FilterContainer,
  Icon,
  IconCircle,
  IconCircleNewCount,
  Line,
  LineWrapper,
  MobileCreateSwitchWrapperShow,
  MobileFilterRow,
  Row,
  SearchArea,
  TicketTypeWrapper,
  filterSelectStyles,
} from './Filter.styles';
import line from './line.svg';
import { useTicketSearchParams } from './useSearchData';

const getSearchAndFilterCount = (filters) => {
  const searchKeys = ['taskId', 'search', 'customerId', 'appointmentId'];
  const filterKeys = ['projectId', 'subprojectId', 'assigneeIds', 'createdByIds'];

  const searchCount = searchKeys.reduce((count, key) => {
    const value = filters[key];
    return count + (typeof value === 'string' && value.trim() !== '' ? 1 : 0);
  }, 0);

  const filterCount =
    filterKeys.reduce((count, key) => {
      const value = filters[key];
      return count + (Array.isArray(value) && value.length > 0 ? 1 : 0);
    }, 0) + (filters.appointmentType && filters.appointmentType !== 'all' ? 1 : 0);

  return { searchCount, filterCount };
};

const Filter = () => {
  const { uuid } = useParams();
  const { searchData, setTicketSearchData, resetSearchData } = useTicketSearchParams();
  const { searchCount, filterCount } = getSearchAndFilterCount(searchData);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [ticketId, setTicketId] = useState(searchData.taskId || '');
  const [ticketTitleDescription, setTicketTitleDescription] = useState(
    searchData.search || ''
  );
  const [customerId, setCustomerId] = useState(searchData.customerId || '');
  const [appointmentId, setAppointmentId] = useState(searchData.appointmentId || '');
  const [createdAtDate, setCreatedAtDate] = useState(searchData.fromDate);
  const [toDate, setToDate] = useState(searchData.toDate);
  const [assignToMeActive, setAssignToMeActive] = useState(searchData.onlyAssignee);
  const [assignToMeNewActive, setAssignToMeNewActive] = useState(searchData.onlyAssigneeNew);
  const [assignToTeamActive, setAssignToTeamActive] = useState(searchData.onlyTeam);
  const [assignToTeamNewActive, setAssignToTeamNewActive] = useState(searchData.onlyTeamNew);
  const [watcherActive, setWatcherActive] = useState(searchData.onlyWatcher);
  const [watcherNewActive, setWatcherNewActive] = useState(searchData.onlyWatcherNew);
  const [filterShowHide, setFilterShowHide] = useState(false);
  const [searchShowHide, setSearchShowHide] = useState(false);
  const [showTooltipMe, setShowTooltipMe] = useState(false);
  const [showTooltipTeam, setShowTooltipTeam] = useState(false);
  const [showTooltipWatcher, setShowTooltipWatcher] = useState(false);

  const [resetAllFilters, setResetAllFilters] = useState([]);
  const [first, setFirst] = useState(false);
  const debouncedTicketId = useDebounce(ticketId, 500);
  const debouncedTicketTitleDescription = useDebounce(ticketTitleDescription, 500);
  const debouncedCustomerId = useDebounce(customerId, 500);
  const debouncedAppointmentId = useDebounce(appointmentId, 500);

  const createdByForFilter = useSelector(selectCreatedByForFilter);
  const projects = useSelector(selectProjectsForFilter);
  const subprojectsForFilter = useSelector(selectSubprojectsForFilter);
  const assigneesForFilter = useSelector(selectAssigneesForFilter);
  const appointmentAssignTypes = useSelector(selectAppointmentAssignTypes);

  // Transform sales projects to match FilterSelect expected format (uuid, name)
  const projectsForFilter = projects || [];

  const handleTicketTitleDescriptionChange = (e) => {
    const value = e.target.value;
    setTicketTitleDescription(value);
    setFirst(true);
  };
  const handleTicketCustomerIdChange = (e) => {
    const value = e.target.value;
    setCustomerId(value);
    setFirst(true);
  };

  const handleAppointmentIdChange = (e) => {
    const value = e.target.value;
    setAppointmentId(value);
    setFirst(true);
  };

  const handleDateChange = (type, date) => {
    const parsedDate = new Date(date);
    if (type === 'from') {
      parsedDate.setHours(0, 0, 0, 0);
      const iso = date ? parsedDate.toISOString() : null;
      setCreatedAtDate(iso);
      setTicketSearchData({ fromDate: iso });
    } else {
      parsedDate.setHours(23, 59, 59, 999);
      const iso = date ? parsedDate.toISOString() : null;
      setToDate(iso);
      setTicketSearchData({ toDate: iso });
    }
  };

  const toggleActiveState = (setter, relatedSetter, fieldName, relatedFieldName) => {
    setter((prev) => {
      const newVal = !prev;

      const newSearchData = { [fieldName]: newVal };

      if (newVal && relatedSetter && relatedFieldName) {
        relatedSetter(false);
        newSearchData[relatedFieldName] = false;
      }

      setTicketSearchData(newSearchData);
      return newVal;
    });
  };

  const handleFilterChange = (key, value) => {
    if (value.length > 0) {
      setTicketSearchData({ [key]: value });
    } else {
      setTicketSearchData({ [key]: undefined });
    }
  };

  const handleProjectSelect = (project) => handleFilterChange('projectId', project);

  const handleSubprojectSelect = (subproject) =>
    handleFilterChange('subprojectId', subproject);

  const handleCreatedBySelect = (createdByIds) =>
    handleFilterChange('createdByIds', createdByIds);

  const handleAssigneeSelect = (assigneeIds) => handleFilterChange('assigneeIds', assigneeIds);

  const handleAppointmentTypeChange = (option) => {
    setTicketSearchData({ appointmentType: option.value });
  };

  const handleCreateNavigate = () => {
    navigate('/project-management/appointment/create-edit', { state: { projectUuid: uuid } });
  };

  const handleFilterSearchShowHide = (type) => {
    if (type === 'filter') {
      setSearchShowHide(false);
      setFilterShowHide((prev) => !prev);
    } else if (type === 'search') {
      setFilterShowHide(false);
      setSearchShowHide((prev) => !prev);
    }
  };

  const resetAllFiltersHandler = () => {
    setSearchShowHide(false);
    setFilterShowHide(false);
    resetSearchData();
    setResetAllFilters([]);
    setCustomerId('');
    setAppointmentId('');
    setTicketId('');
    setTicketTitleDescription('');
    setCreatedAtDate(null);
    setToDate(null);
    setAssignToMeActive(false);
    setAssignToMeNewActive(false);
    setAssignToTeamActive(false);
    setAssignToTeamNewActive(false);
    setWatcherActive(false);
    setWatcherNewActive(false);
  };

  useEffect(() => {
    if (first) {
      setTicketSearchData({ taskId: debouncedTicketId });
    }
  }, [debouncedTicketId, first]);

  useEffect(() => {
    if (first) setTicketSearchData({ search: debouncedTicketTitleDescription });
  }, [debouncedTicketTitleDescription, first]);

  useEffect(() => {
    if (first) setTicketSearchData({ customerId: debouncedCustomerId });
  }, [debouncedCustomerId, first]);

  useEffect(() => {
    if (first) setTicketSearchData({ appointmentId: debouncedAppointmentId });
  }, [debouncedAppointmentId, first]);

  useEffect(() => {
    dispatch(getProjectsForFilter());
    dispatch(getSubprojectsForFilter());
    dispatch(getAssigneesForFilter());
    dispatch(getCreatedByForFilter());
  }, [dispatch]);

  const projectValue = (searchData.projectId || []).map((value) => {
    const matched = projectsForFilter.find((option) => option.uuid === value);
    return { id: matched ? matched.uuid : value, label: matched ? matched.name : value };
  });
  const subprojectValue = searchData.subprojectId.map((value) => {
    const matched = subprojectsForFilter.find((option) => option.uuid === value);
    return { id: matched ? matched.uuid : value, label: matched ? matched.name : value };
  });
  const createdByIdsValue = searchData.createdByIds.map((value) => {
    const matched = createdByForFilter.find((option) => option.uuid === value);
    return { id: matched ? matched.uuid : value, label: matched ? matched.name : value };
  });

  const assigneeValue = searchData.assigneeIds.map((value) => {
    const matched = assigneesForFilter.find((option) => option.uuid === value);
    return { id: matched ? matched.uuid : value, label: matched ? matched.name : value };
  });

  const handleClickClearSearch = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setTicketId('');
    setTicketTitleDescription('');
    setCustomerId('');
    setAppointmentId('');

    setTicketSearchData({
      taskId: '',
      search: '',
      customerId: '',
      appointmentId: '',
    });
  };

  const handleClickClearFilter = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setResetAllFilters([]);

    setTicketSearchData({
      projectId: [],
      subprojectId: [],
      assigneeIds: [],
      createdByIds: [],
      appointmentType: 'all',
    });
  };

  const handleClearAll = () => {
    setTicketSearchData({ taskId: '', search: '', customerId: '', appointmentId: '' });
    setTicketId('');
    setTicketTitleDescription('');
    setCustomerId('');
    setAppointmentId('');
  };

  return (
    <FilterContainer>
      <ActionsWrapper>
        <CalendarTypeWrapper>
          <CalendarWrapper>
            <CustomDatePicker
              height="38px"
              placeholder="Created date from"
              value={createdAtDate ? dayjs(createdAtDate) : null}
              maxDate={toDate ? dayjs(toDate) : null}
              onChange={(date) => handleDateChange('from', date)}
            />
            <CustomDatePicker
              height="38px"
              placeholder="Created date to"
              value={toDate ? dayjs(toDate) : null}
              minDate={createdAtDate ? dayjs(createdAtDate) : null}
              onChange={(date) => handleDateChange('to', date)}
            />
          </CalendarWrapper>

          <TicketTypeWrapper>
            <Row>
              <IconCircle
                $isActive={assignToMeActive}
                onClick={() =>
                  toggleActiveState(
                    setAssignToMeActive,
                    setAssignToMeNewActive,
                    'onlyAssignee',
                    'onlyAssigneeNew'
                  )
                }
                onMouseEnter={() => setShowTooltipMe(true)}
                onMouseLeave={() => setShowTooltipMe(false)}
              >
                <TooltipText $isVisible={showTooltipMe}>Assigned to me</TooltipText>
                <Icon src={assignToMeActive ? userActive : user} alt="user" />
                {appointmentAssignTypes.myAssigned > 0 && (
                  <IconCircleNewCount>
                    {appointmentAssignTypes.myAssigned > 9
                      ? '9+'
                      : appointmentAssignTypes.myAssigned}
                  </IconCircleNewCount>
                )}
              </IconCircle>
              <Line />
              <IconCircle
                $isNew
                $isActive={assignToMeNewActive}
                onClick={() =>
                  toggleActiveState(
                    setAssignToMeNewActive,
                    setAssignToMeActive,
                    'onlyAssigneeNew',
                    'onlyAssignee'
                  )
                }
              >
                New
                {appointmentAssignTypes.myAssignedNew > 0 && (
                  <IconCircleNewCount $type="new">
                    {appointmentAssignTypes.myAssignedNew > 9
                      ? '9+'
                      : appointmentAssignTypes.myAssignedNew}
                  </IconCircleNewCount>
                )}
              </IconCircle>
            </Row>
            <Row>
              <IconCircle
                $isActive={assignToTeamActive}
                onClick={() =>
                  toggleActiveState(
                    setAssignToTeamActive,
                    setAssignToTeamNewActive,
                    'onlyTeam',
                    'onlyTeamNew'
                  )
                }
                onMouseEnter={() => setShowTooltipTeam(true)}
                onMouseLeave={() => setShowTooltipTeam(false)}
              >
                <TooltipText $isVisible={showTooltipTeam}>Assigned to team</TooltipText>
                <Icon src={assignToTeamActive ? usersActive : users} alt="users" />
                {appointmentAssignTypes.myTeamTickets > 0 && (
                  <IconCircleNewCount>
                    {appointmentAssignTypes.myTeamTickets > 9
                      ? '9+'
                      : appointmentAssignTypes.myTeamTickets}
                  </IconCircleNewCount>
                )}
              </IconCircle>
              <Line />
              <IconCircle
                $isNew
                $isActive={assignToTeamNewActive}
                onClick={() =>
                  toggleActiveState(
                    setAssignToTeamNewActive,
                    setAssignToTeamActive,
                    'onlyTeamNew',
                    'onlyTeam'
                  )
                }
              >
                New
                {appointmentAssignTypes.myTeamNewTickets > 0 && (
                  <IconCircleNewCount $type="new">
                    {appointmentAssignTypes.myTeamNewTickets > 9
                      ? '9+'
                      : appointmentAssignTypes.myTeamNewTickets}
                  </IconCircleNewCount>
                )}
              </IconCircle>
            </Row>
            <Row>
              <IconCircle
                $isActive={watcherActive}
                onClick={() =>
                  toggleActiveState(
                    setWatcherActive,
                    setWatcherNewActive,
                    'onlyWatcher',
                    'onlyWatcherNew'
                  )
                }
                onMouseEnter={() => setShowTooltipWatcher(true)}
                onMouseLeave={() => setShowTooltipWatcher(false)}
              >
                <TooltipText $isVisible={showTooltipWatcher}>Watcher</TooltipText>
                <Icon src={watcherActive ? watchActive : watch} alt="watch" />
                {appointmentAssignTypes.myWatcher > 0 && (
                  <IconCircleNewCount>
                    {appointmentAssignTypes.myWatcher > 9
                      ? '9+'
                      : appointmentAssignTypes.myWatcher}
                  </IconCircleNewCount>
                )}
              </IconCircle>
              <Line />
              <IconCircle
                $isNew
                $isActive={watcherNewActive}
                onClick={() =>
                  toggleActiveState(
                    setWatcherNewActive,
                    setWatcherActive,
                    'onlyWatcherNew',
                    'onlyWatcher'
                  )
                }
              >
                New
                {appointmentAssignTypes.myWatcherNew > 0 && (
                  <IconCircleNewCount $type="new">
                    {appointmentAssignTypes.myWatcherNew > 9
                      ? '9+'
                      : appointmentAssignTypes.myWatcherNew}
                  </IconCircleNewCount>
                )}
              </IconCircle>
            </Row>
          </TicketTypeWrapper>
        </CalendarTypeWrapper>
        <FilterAllWrapper>
          <FilterActionsWrapper>
            <Button
              outlined
              variant="filter"
              active={filterCount > 0}
              clearable={filterCount > 0}
              onClear={handleClickClearFilter}
              onClick={() => handleFilterSearchShowHide('filter')}
              className="filter-trigger-button"
            >
              Filter {filterCount > 0 ? ` (${filterCount})` : ''}
            </Button>

            <Button
              outlined
              variant="search"
              active={searchCount > 0}
              clearable={searchCount > 0}
              onClear={handleClickClearSearch}
              onClick={() => handleFilterSearchShowHide('search')}
              className="search-trigger-button"
            >
              Search {searchCount > 0 ? ` (${searchCount})` : ''}
            </Button>
            <Button secondary onClick={handleCreateNavigate} className="create-button">
              + Create appointment
            </Button>
          </FilterActionsWrapper>
          <MobileCreateSwitchWrapperShow>
            <ResetButton onClick={resetAllFiltersHandler} />
          </MobileCreateSwitchWrapperShow>
        </FilterAllWrapper>
      </ActionsWrapper>

      {searchShowHide && (
        <>
          <SearchArea>
            <Input
              label="Appointment title or description"
              value={ticketTitleDescription}
              onChange={handleTicketTitleDescriptionChange}
            />
            <Input
              label="Customer ID"
              type="number"
              value={customerId}
              onChange={handleTicketCustomerIdChange}
            />
            <Input
              label="Appointment ID"
              type="number"
              value={appointmentId}
              onChange={handleAppointmentIdChange}
            />
            <Column>
              <CloseIconWrapper onClick={() => handleFilterSearchShowHide('search')}>
                <Icon src={close} />
              </CloseIconWrapper>
              <ClearAllText onClick={handleClearAll}>Clear All</ClearAllText>
            </Column>
          </SearchArea>
          <BottomSheet>
            <LineWrapper onClick={() => handleFilterSearchShowHide('search')}>
              <Icon src={line} />
            </LineWrapper>
            <Input
              label="Appointment title or description"
              value={ticketTitleDescription}
              onChange={handleTicketTitleDescriptionChange}
            />
            <Input
              label="Customer ID"
              type="number"
              value={customerId}
              onChange={handleTicketCustomerIdChange}
            />
            <Input
              label="Appointment ID"
              type="number"
              value={appointmentId}
              onChange={handleAppointmentIdChange}
            />
            <CalendarWrapper className="showBelow767px">
              <CustomDatePicker
                height="38px"
                placeholder="Created date from"
                onChange={(date) => handleDateChange('from', date)}
              />
              <CustomDatePicker
                className="dateTo767"
                height="38px"
                placeholder="Created date to"
                onChange={(date) => handleDateChange('to', date)}
              />
            </CalendarWrapper>
          </BottomSheet>
        </>
      )}

      {filterShowHide && (
        <>
          <BottomSheet>
            <LineWrapper onClick={() => handleFilterSearchShowHide('filter')}>
              <Icon src={line} />
            </LineWrapper>
            <FilterSelect
              title="Project"
              allTitle="All projects"
              options={projectsForFilter}
              onSelect={handleProjectSelect}
              resetRegions={resetAllFilters}
              defaultValue={projectValue}
              isActive={!!projectValue.length}
              showSearch
            />
            <FilterSelect
              title="Subproject"
              allTitle="All subprojects"
              options={subprojectsForFilter}
              onSelect={handleSubprojectSelect}
              resetRegions={resetAllFilters}
              defaultValue={subprojectValue}
              isActive={!!subprojectValue.length}
              showSearch
            />
            <MobileFilterRow className="double-select-wrapper">
              <FilterSelect
                title="Assignee"
                allTitle="All assignees"
                options={assigneesForFilter}
                onSelect={handleAssigneeSelect}
                resetRegions={resetAllFilters}
                defaultValue={assigneeValue}
                isActive={!!assigneeValue.length}
                showSearch
              />
              <FilterSelect
                title="Created by"
                allTitle="All"
                options={createdByForFilter}
                onSelect={handleCreatedBySelect}
                resetRegions={resetAllFilters}
                defaultValue={createdByIdsValue}
                isActive={!!createdByIdsValue.length}
                showSearch
              />
            </MobileFilterRow>
            <AppointmentTypeSelectWrapper>
              <Select
                options={APPOINTMENT_TYPE_OPTIONS}
                value={APPOINTMENT_TYPE_OPTIONS.find(
                  (o) => o.value === searchData.appointmentType
                )}
                onChange={handleAppointmentTypeChange}
                styles={filterSelectStyles(searchData.appointmentType !== 'all')}
                isSearchable={false}
              />
            </AppointmentTypeSelectWrapper>
          </BottomSheet>
          <SearchArea>
            <FilterSelect
              title="Project"
              allTitle="All projects"
              options={projectsForFilter}
              onSelect={handleProjectSelect}
              resetRegions={resetAllFilters}
              defaultValue={projectValue}
              isActive={!!projectValue.length}
              showSearch
            />
            <FilterSelect
              title="Subproject"
              allTitle="All subprojects"
              options={subprojectsForFilter}
              onSelect={handleSubprojectSelect}
              resetRegions={resetAllFilters}
              defaultValue={subprojectValue}
              isActive={!!subprojectValue.length}
              showSearch
            />
            <FilterSelect
              title="Assignee"
              allTitle="All assignees"
              options={assigneesForFilter}
              onSelect={handleAssigneeSelect}
              resetRegions={resetAllFilters}
              defaultValue={assigneeValue}
              isActive={!!assigneeValue.length}
              showSearch
            />
            <FilterSelect
              title="Created by"
              allTitle="All"
              options={createdByForFilter}
              onSelect={handleCreatedBySelect}
              resetRegions={resetAllFilters}
              defaultValue={createdByIdsValue}
              isActive={!!createdByIdsValue.length}
              showSearch
            />
            <AppointmentTypeSelectWrapper>
              <Select
                options={APPOINTMENT_TYPE_OPTIONS}
                value={APPOINTMENT_TYPE_OPTIONS.find(
                  (o) => o.value === searchData.appointmentType
                )}
                onChange={handleAppointmentTypeChange}
                styles={filterSelectStyles(searchData.appointmentType !== 'all')}
                isSearchable={false}
              />
            </AppointmentTypeSelectWrapper>
            <Column>
              <CloseIconWrapper onClick={() => handleFilterSearchShowHide('filter')}>
                <Icon src={close} />
              </CloseIconWrapper>
              <ClearAllText onClick={resetAllFiltersHandler}>Clear All</ClearAllText>
            </Column>
          </SearchArea>
        </>
      )}
    </FilterContainer>
  );
};

export default Filter;
